import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from 'date-fns';
import {
  Clock,
  LogIn,
  LogOut,
  CalendarDays,
  CheckCircle,
  XCircle,
  Timer,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeAttendancePage = () => {
  const [loading, setLoading] = useState(true);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('employeeUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchAttendance();
    }
  }, [user, selectedMonth, selectedYear]);

  const fetchAttendance = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const [todayRes, historyRes] = await Promise.all([
        axios.get(`${API}/attendance`, { params: { employee_id: user.id, date: today } }),
        axios.get(`${API}/attendance`, { params: { employee_id: user.id } })
      ]);

      setTodayAttendance(todayRes.data?.[0] || null);
      setAttendanceHistory(historyRes.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      const response = await axios.post(`${API}/attendance/check-in`);
      toast.success(`Checked in at ${response.data.time}`);
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error checking in');
    }
  };

  const handleCheckOut = async () => {
    try {
      const response = await axios.post(`${API}/attendance/check-out`);
      toast.success(`Checked out! Total: ${response.data.total_hours} hours`);
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error checking out');
    }
  };

  // Filter attendance for selected month
  const filteredAttendance = attendanceHistory.filter(record => {
    const date = new Date(record.date);
    return date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
  });

  // Calculate stats
  const stats = {
    presentDays: filteredAttendance.filter(a => a.status === 'present').length,
    absentDays: filteredAttendance.filter(a => a.status === 'absent').length,
    totalHours: filteredAttendance.reduce((sum, a) => sum + (a.total_hours || 0), 0),
    avgHours: filteredAttendance.length > 0 
      ? (filteredAttendance.reduce((sum, a) => sum + (a.total_hours || 0), 0) / filteredAttendance.filter(a => a.total_hours > 0).length).toFixed(1)
      : 0
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="employee-attendance-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-brand-navy font-heading">My Attendance</h1>
        <p className="text-slate-500 mt-1">Track your daily attendance and work hours</p>
      </div>

      {/* Today's Attendance Card */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-brand-navy to-brand-navy-light text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-1">Today's Status</h3>
              <p className="text-slate-300 text-sm">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
              {todayAttendance && (
                <div className="flex items-center gap-4 mt-3">
                  {todayAttendance.check_in && (
                    <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                      <LogIn className="h-4 w-4 text-emerald-400" />
                      <span className="text-sm">In: {todayAttendance.check_in}</span>
                    </div>
                  )}
                  {todayAttendance.check_out && (
                    <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                      <LogOut className="h-4 w-4 text-amber-400" />
                      <span className="text-sm">Out: {todayAttendance.check_out}</span>
                    </div>
                  )}
                  {todayAttendance.total_hours > 0 && (
                    <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                      <Timer className="h-4 w-4 text-blue-400" />
                      <span className="text-sm">{todayAttendance.total_hours} hours</span>
                    </div>
                  )}
                </div>
              )}
              {!todayAttendance && (
                <p className="text-amber-300 text-sm mt-3">You haven't marked attendance today</p>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCheckIn}
                data-testid="attendance-check-in-btn"
                disabled={todayAttendance?.check_in}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Check In
              </Button>
              <Button
                onClick={handleCheckOut}
                data-testid="attendance-check-out-btn"
                disabled={!todayAttendance?.check_in || todayAttendance?.check_out}
                className="bg-amber-500 hover:bg-amber-600 text-white px-6"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Check Out
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Present Days</p>
                <p className="text-xl font-bold text-brand-navy">{stats.presentDays}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Absent Days</p>
                <p className="text-xl font-bold text-brand-navy">{stats.absentDays}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Hours</p>
                <p className="text-xl font-bold text-brand-navy">{stats.totalHours.toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-gold/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-brand-gold" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Avg Hours/Day</p>
                <p className="text-xl font-bold text-brand-navy">{stats.avgHours}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attendance History */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Attendance History
          </CardTitle>
          <div className="flex gap-2">
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[130px]" data-testid="month-select">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month, index) => (
                  <SelectItem key={index} value={index.toString()}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]" data-testid="year-select">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredAttendance.length === 0 ? (
            <div className="text-center py-12">
              <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No attendance records for {months[selectedMonth]} {selectedYear}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Check In</TableHead>
                    <TableHead>Check Out</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAttendance.map((record) => {
                    const date = new Date(record.date);
                    return (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{format(date, 'dd MMM yyyy')}</TableCell>
                        <TableCell>{format(date, 'EEEE')}</TableCell>
                        <TableCell>
                          {record.check_in ? (
                            <span className="flex items-center gap-1">
                              <LogIn className="h-3 w-3 text-emerald-500" />
                              {record.check_in}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {record.check_out ? (
                            <span className="flex items-center gap-1">
                              <LogOut className="h-3 w-3 text-amber-500" />
                              {record.check_out}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {record.total_hours > 0 ? `${record.total_hours} hrs` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            record.status === 'present'
                              ? 'bg-emerald-100 text-emerald-700 border-0'
                              : record.status === 'half-day'
                              ? 'bg-amber-100 text-amber-700 border-0'
                              : 'bg-red-100 text-red-700 border-0'
                          }>
                            {record.status === 'present' ? 'Present' : record.status === 'half-day' ? 'Half Day' : 'Absent'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeAttendancePage;
